/*
 * BunnyRest – pristatymo pasirinkimas krepšelyje: paštomatas arba kurjeris (Omniva / LP Express / DPD).
 * Pasirinkimas išsaugomas kaip krepšelio atributai ir matomas užsakyme
 * („Papildoma informacija“ / „Additional details“).
 */
(() => {
  if (customElements.get('parcel-locker-picker')) return;

  const CARRIERS = {
    omniva: { label: 'Omniva' },
    lpexpress: { label: 'LP Express' },
    dpd: { label: 'DPD' },
  };
  const TYPES = {
    locker: { label: 'Paštomatas', rate: 'Pristatymas į paštomatą' },
    courier: { label: 'Kurjeris', rate: 'Kurjeriu į namus' },
  };
  const ATTR = { type: 'Pristatymo būdas', carrier: 'Kurjeris', locker: 'Paštomatas', id: 'Paštomato ID' };
  const OMNIVA_URL = 'https://www.omniva.lt/locations.json';
  const OMNIVA_CACHE = 'bunnyrest-omniva-lt-v2';
  const MAX_RESULTS = 40;

  const norm = (s) =>
    (s || '')
      .toString()
      .toLowerCase()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/\s+/g, ' ')
      .trim();

  const escapeHtml = (s) =>
    (s || '').toString().replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

  const fullAddress = (l) => (!l.city || l.address.includes(l.city) ? l.address : `${l.address}, ${l.city}`);

  // ---------- Duomenys ----------
  const state = { type: '', carrier: '', locker: '', id: '' };
  const isComplete = () =>
    (state.type === 'locker' && state.carrier && state.locker) || (state.type === 'courier' && state.carrier);
  let dataPromise = null;

  function withIndex(rows) {
    return (rows || []).map(([id, name, city, address]) => ({
      id: String(id),
      name,
      city,
      address,
      search: norm(`${name} ${city} ${address}`),
    }));
  }

  function loadOmniva() {
    try {
      const cached = sessionStorage.getItem(OMNIVA_CACHE);
      if (cached) return Promise.resolve(JSON.parse(cached));
    } catch (e) {}
    return fetch(OMNIVA_URL)
      .then((r) => {
        if (!r.ok) throw new Error('Omniva ' + r.status);
        return r.json();
      })
      .then((all) => {
        const rows = all
          .filter((x) => x.A0_NAME === 'LT' && x.TYPE === '0')
          .map((x) => {
            const city = x.A3_NAME || x.A2_NAME || '';
            const address = [x.A5_NAME, x.A7_NAME].filter(Boolean).join(' ').trim();
            return [x.ZIP, x.NAME.replace(/ paštomatas$/i, ''), city, address];
          })
          .sort((a, b) => a[2].localeCompare(b[2], 'lt') || a[1].localeCompare(b[1], 'lt'));
        try {
          sessionStorage.setItem(OMNIVA_CACHE, JSON.stringify(rows));
        } catch (e) {}
        return rows;
      });
  }

  function loadData(staticUrl) {
    if (!dataPromise) {
      const staticData = fetch(staticUrl).then((r) => r.json());
      dataPromise = Promise.all([staticData, loadOmniva().catch(() => null)]).then(([s, omniva]) => ({
        lpexpress: withIndex(s.lpexpress),
        dpd: withIndex(s.dpd),
        omniva: omniva ? withIndex(omniva) : null,
      }));
      dataPromise.catch(() => {
        dataPromise = null;
      });
    }
    return dataPromise;
  }

  function saveAttributes(attrs) {
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return fetch(root + 'cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes: attrs }),
    });
  }

  // ---------- Komponentas ----------
  function currentAttributes() {
    return {
      [ATTR.type]: state.type ? TYPES[state.type].label : '',
      [ATTR.carrier]: state.carrier ? CARRIERS[state.carrier].label : '',
      [ATTR.locker]: state.type === 'locker' ? state.locker : '',
      [ATTR.id]: state.type === 'locker' ? state.id : '',
      Pristatymas: '', // senas raktas – išvalomas
    };
  }

  class ParcelLockerPicker extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;

      // Būsena iš Liquid (krepšelio atributai) – po krepšelio perpiešimo nepasimeta.
      if (this.dataset.type && !this.constructor._hydrated) {
        state.type = this.dataset.type;
        state.carrier = this.dataset.carrierKey || '';
        state.locker = this.dataset.locker || '';
        state.id = this.dataset.lockerId || '';
      }
      this.constructor._hydrated = true;

      this.typeButtons = [...this.querySelectorAll('[data-plp-type]')];
      this.carriersWrap = this.querySelector('.plp__carriers');
      this.carrierButtons = [...this.querySelectorAll('[data-plp-carrier]')];
      this.searchWrap = this.querySelector('.plp__search');
      this.input = this.querySelector('.plp__input');
      this.results = this.querySelector('.plp__results');
      this.selectedWrap = this.querySelector('.plp__selected');
      this.selectedText = this.querySelector('.plp__selected-text');
      this.changeBtn = this.querySelector('.plp__change');
      this.hintEl = this.querySelector('.plp__hint');
      this.errorEl = this.querySelector('.plp__error');
      this.statusEl = this.querySelector('.plp__status');

      this.typeButtons.forEach((btn) => btn.addEventListener('click', () => this.chooseType(btn.dataset.plpType)));
      this.carrierButtons.forEach((btn) =>
        btn.addEventListener('click', () => this.chooseCarrier(btn.dataset.plpCarrier))
      );
      this.input.addEventListener('input', () => this.renderResults());
      this.input.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') e.preventDefault();
      });
      this.results.addEventListener('click', (e) => {
        const li = e.target.closest('[data-plp-index]');
        if (li) this.chooseLocker(this._matches[Number(li.dataset.plpIndex)]);
      });
      this.changeBtn.addEventListener('click', () => this.reopenSearch());

      this.render();
    }

    render() {
      const mark = (btns, attr, value) =>
        btns.forEach((btn) => {
          const active = btn.dataset[attr] === value;
          btn.classList.toggle('is-active', active);
          btn.setAttribute('aria-checked', active ? 'true' : 'false');
        });
      mark(this.typeButtons, 'plpType', state.type);
      mark(this.carrierButtons, 'plpCarrier', state.carrier);

      this.carriersWrap.hidden = !state.type;
      const lockerDone = state.type === 'locker' && state.carrier && state.locker;
      const courierDone = state.type === 'courier' && state.carrier;
      const searching = state.type === 'locker' && state.carrier && !state.locker;

      if (!searching || this.searchWrap.hidden) {
        this.searchWrap.hidden = !searching;
        if (searching) this.renderResults();
      }

      this.selectedWrap.hidden = !(lockerDone || courierDone);
      this.changeBtn.hidden = !lockerDone;
      if (lockerDone) {
        this.selectedText.innerHTML = `<strong>${escapeHtml(CARRIERS[state.carrier].label)} paštomatas:</strong><br>${escapeHtml(state.locker)}`;
      } else if (courierDone) {
        this.selectedText.innerHTML = `<strong>${escapeHtml(CARRIERS[state.carrier].label)} kurjeris</strong> pristatys į adresą, kurį nurodysite atsiskaitydami.`;
      }

      this.hintEl.hidden = !isComplete();
      if (isComplete()) {
        this.hintEl.textContent = `Atsiskaitydami pasirinkite siuntimo būdą „${TYPES[state.type].rate}“.`;
        this.hideError();
      }
    }

    save() {
      return saveAttributes(currentAttributes()).catch(() =>
        this.showError('Nepavyko išsaugoti pasirinkimo. Bandykite dar kartą.')
      );
    }

    chooseType(type) {
      if (!TYPES[type] || type === state.type) return;
      state.type = type;
      state.locker = '';
      state.id = '';
      this.save();
      this.syncAll();
    }

    chooseCarrier(carrier) {
      if (!CARRIERS[carrier]) return;
      if (carrier !== state.carrier) {
        state.carrier = carrier;
        state.locker = '';
        state.id = '';
        this.save();
      }
      this.syncAll();
      if (state.type === 'locker') {
        this.searchWrap.hidden = false;
        this.input.value = '';
        this.renderResults();
        this.input.focus({ preventScroll: true });
      }
    }

    reopenSearch() {
      this.selectedWrap.hidden = true;
      this.hintEl.hidden = true;
      this.searchWrap.hidden = false;
      this.input.value = '';
      this.renderResults();
      this.input.focus({ preventScroll: true });
    }

    async renderResults() {
      const carrier = state.carrier;
      const q = norm(this.input.value);
      this.results.innerHTML = '';
      this._matches = [];
      if (!carrier || state.type !== 'locker') return;

      this.setStatus('Kraunami paštomatai…');
      let data;
      try {
        data = await loadData(this.dataset.src);
      } catch (e) {
        this.setStatus('Nepavyko įkelti paštomatų sąrašo. Perkraukite puslapį.');
        return;
      }
      if (carrier !== state.carrier || q !== norm(this.input.value)) return; // vartotojas jau pakeitė

      const list = data[carrier];
      if (!list) {
        this.setStatus(`Nepavyko įkelti ${CARRIERS[carrier].label} paštomatų. Pasirinkite kitą kurjerį arba perkraukite puslapį.`);
        return;
      }
      if (!q) {
        this.setStatus(`Įveskite miestą arba adresą (${list.length} paštomatai).`);
        return;
      }

      // Linksnių tolerancija: „vilnius“ randa ir „Vilniaus m.“, „kaunas“ – „Kauno“.
      const tokens = q.split(' ').map((t) => (t.length >= 6 ? t.slice(0, -2) : t));
      const matches = list.filter((l) => tokens.every((t) => l.search.includes(t)));
      this._matches = matches.slice(0, MAX_RESULTS);
      if (!matches.length) {
        this.setStatus('Nieko nerasta. Pabandykite įvesti miesto pavadinimą.');
        return;
      }
      this.setStatus(matches.length > MAX_RESULTS ? `Rodomi ${MAX_RESULTS} iš ${matches.length}. Patikslinkite paiešką.` : '');
      this.results.innerHTML = this._matches
        .map(
          (l, i) => `<li role="option" tabindex="0" class="plp__result" data-plp-index="${i}">
            <span class="plp__result-name">${escapeHtml(l.name)}</span>
            <span class="plp__result-address">${escapeHtml(fullAddress(l))}</span>
          </li>`
        )
        .join('');
      this.results.querySelectorAll('.plp__result').forEach((li) =>
        li.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            li.click();
          }
        })
      );
    }

    chooseLocker(locker) {
      if (!locker) return;
      state.locker = `${locker.name}, ${fullAddress(locker)}`;
      state.id = locker.id.startsWith('dpd') ? '' : locker.id;
      this.searchWrap.hidden = true;
      this.save();
      this.syncAll();
    }

    setStatus(text) {
      this.statusEl.textContent = text;
      this.statusEl.hidden = !text;
    }

    showError(text) {
      if (text) this.errorEl.textContent = text;
      this.errorEl.hidden = false;
    }

    hideError() {
      this.errorEl.hidden = true;
    }

    syncAll() {
      document.querySelectorAll('parcel-locker-picker').forEach((el) => el._ready && el.render());
    }
  }

  customElements.define('parcel-locker-picker', ParcelLockerPicker);

  // ---------- Neleisti eiti į apmokėjimą be paštomato ----------
  function blockIfMissing(event) {
    const pickers = [...document.querySelectorAll('parcel-locker-picker')];
    if (!pickers.length || isComplete()) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    const visible = pickers.find((p) => p.offsetParent !== null) || pickers[0];
    const msg = state.type === 'locker' && state.carrier ? 'Pasirinkite paštomatą, kad galėtumėte tęsti.' : 'Pasirinkite pristatymo būdą, kad galėtumėte tęsti.';
    pickers.forEach((p) => p.showError && p.showError(msg));
    visible.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  document.addEventListener(
    'click',
    (e) => {
      if (e.target.closest('button[name="checkout"], input[name="checkout"], a[href$="/checkout"]')) blockIfMissing(e);
    },
    true
  );
  document.addEventListener(
    'submit',
    (e) => {
      const submitter = e.submitter;
      if (submitter && submitter.name === 'checkout') blockIfMissing(e);
    },
    true
  );
})();
